import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Cake, Sparkles, X, Copy } from "lucide-react";
import api from "@/lib/api";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function daysInMonth(month) {
  // 1-indexed month; safely cap (use 2025 = non-leap for Feb 29 safety)
  const d = new Date(2025, month, 0).getDate();
  return d;
}

/**
 * BirthdayRewardCard — surfaces birthday state on the customer dashboard.
 *
 * Three states:
 *   1. Coupon active → celebratory card with code + Shop Current Drops CTA
 *   2. Birthday on file (waiting) → quiet card with month info
 *   3. No birthday → prompt to add (M/D inline form)
 */
export default function BirthdayRewardCard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [month, setMonth] = useState(1);
  const [day, setDay] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/birthday-reward");
      setData(data);
      if (data?.birthday_mmdd) {
        setMonth(parseInt(data.birthday_mmdd.slice(0, 2), 10));
        setDay(parseInt(data.birthday_mmdd.slice(3, 5), 10));
      }
    } catch (e) {
      // 401 etc — silent
      setData({ enabled: false });
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!data) return null;
  if (!data.enabled && !data.coupon) return null; // feature off + no legacy coupon

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.put("/me/birthday", { month, day });
      setEditing(false);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not save birthday.");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Remove your birthday from your profile?")) return;
    setBusy(true);
    try {
      await api.delete("/me/birthday");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // ─── State 1: active coupon ───
  if (data.active && data.coupon) {
    const c = data.coupon;
    return (
      <div
        className="rounded-2xl p-6 lg:p-7 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #1A4331 0%, #245540 60%, #2D6951 100%)",
          boxShadow: "0 25px 60px -30px rgba(26,67,49,0.6)",
        }}
        data-testid="birthday-card-active"
      >
        <div
          aria-hidden
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(circle, #F4A261, transparent 70%)" }}
        />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: "#F4A261" }}>
            <Cake size={14} /> Birthday reward
          </div>
          <h3 className="mt-3 font-display text-2xl lg:text-3xl text-white leading-tight">
            Your Birthday Reward Is Here
          </h3>
          <p className="mt-3 text-white/80 text-sm leading-relaxed max-w-md">
            Happy Birthday from The Farm To You. Enjoy this special reward on
            your next order.
          </p>

          <div
            className="mt-5 inline-flex items-center gap-3 rounded-xl px-4 py-3 bg-white/10 border border-white/15"
            data-testid="birthday-coupon-code"
          >
            <span className="font-display text-2xl text-white">{c.human_amount}</span>
            <span className="text-white/30">·</span>
            <code className="font-mono text-sm bg-white text-[var(--ff-primary)] px-3 py-1.5 rounded-md">
              {c.code}
            </code>
            <button
              type="button"
              onClick={() => copy(c.code)}
              className="text-white/80 hover:text-white"
              aria-label="Copy code"
              data-testid="birthday-copy-code"
            >
              <Copy size={14} />
            </button>
          </div>
          {copied && <p className="mt-2 text-xs text-[#F4A261]">Copied to clipboard.</p>}
          <p className="mt-3 text-xs text-white/60">
            Expires {new Date(c.expires_at).toLocaleDateString()}
          </p>

          <Link
            to="/drops"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[var(--ff-primary)] hover:-translate-y-0.5 transition-transform"
            style={{ background: "#F4A261" }}
            data-testid="birthday-shop-cta"
          >
            <Sparkles size={14} /> Shop Current Drops
          </Link>
        </div>
      </div>
    );
  }

  // ─── State 2: birthday on file, waiting ───
  if (data.birthday_mmdd && !editing) {
    const monthName = MONTHS[parseInt(data.birthday_mmdd.slice(0, 2), 10) - 1];
    const dayNum = parseInt(data.birthday_mmdd.slice(3, 5), 10);
    return (
      <div
        className="rounded-2xl p-6 border border-[var(--ff-border)] bg-white"
        data-testid="birthday-card-waiting"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-secondary)]">
              <Gift size={14} /> Birthday reward
            </div>
            <h3 className="mt-2 font-display text-2xl text-[var(--ff-text-primary)]">
              You're on the birthday list.
            </h3>
            <p className="mt-2 text-sm text-[var(--ff-text-secondary)] leading-relaxed max-w-md">
              We'll send your special Farm To You reward in <strong>{monthName}</strong>{" "}
              ({monthName} {dayNum}). It will appear here and in your inbox.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs text-[var(--ff-primary)] underline"
            data-testid="birthday-edit"
          >
            Edit
          </button>
        </div>
        <button
          type="button"
          onClick={clear}
          className="mt-4 text-xs text-[var(--ff-text-muted)] hover:text-rose-700 inline-flex items-center gap-1"
          data-testid="birthday-clear"
        >
          <X size={12} /> Remove birthday
        </button>
      </div>
    );
  }

  // ─── State 3: no birthday on file → prompt or edit form ───
  return (
    <div
      className="rounded-2xl p-6 border border-[var(--ff-border)] bg-white"
      data-testid="birthday-card-prompt"
    >
      <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-secondary)]">
        <Gift size={14} /> Birthday reward
      </div>
      <h3 className="mt-2 font-display text-2xl text-[var(--ff-text-primary)]">
        Get a Birthday Reward
      </h3>
      <p className="mt-2 text-sm text-[var(--ff-text-secondary)] leading-relaxed max-w-md">
        Add your birthday and we'll send you a special Farm To You coupon during
        your birthday month.
      </p>

      {!editing ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="btn-primary mt-5 inline-flex"
          data-testid="birthday-add-btn"
        >
          Add My Birthday
        </button>
      ) : (
        <div className="mt-5 space-y-3" data-testid="birthday-form">
          <div className="flex gap-3 flex-wrap">
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] block mb-1">
                Month
              </label>
              <select
                className="input-base"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                data-testid="birthday-month"
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] block mb-1">
                Day
              </label>
              <select
                className="input-base"
                value={day}
                onChange={(e) => setDay(parseInt(e.target.value, 10))}
                data-testid="birthday-day"
              >
                {Array.from({ length: daysInMonth(month) }, (_, i) => i + 1).map(
                  (d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>
          <p className="text-xs text-[var(--ff-text-muted)] italic max-w-md">
            We only need your month and day — no birth year required. Birthdays
            are used solely to send your member reward.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="btn-primary"
              data-testid="birthday-save-btn"
            >
              {busy ? "Saving…" : "Save birthday"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="btn-secondary"
              data-testid="birthday-cancel-btn"
            >
              Cancel
            </button>
          </div>
          {err && (
            <p className="text-xs text-rose-700" data-testid="birthday-err">
              {err}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
